// HR salary calculator — the pure arithmetic behind the Stipends page.
//
// Policy: only days actually attended are paid. Working days exclude weekly
// offs (Sundays) and official holidays; approved leave and unexcused absences
// are both unpaid. Manual reductions apply after the attendance earning.
//
//   Daily Stipend         = Monthly Stipend / Total Working Days
//   Final Payable Stipend = Daily Stipend × Present Days − Reductions
//   (Absence Deduction    = Daily Stipend × Absent Working Days — reported
//    for the stipend report; leave days are simply never earned.)

import { isWeekendYMD } from '@/lib/date-utils'

export interface SalaryCalcInput {
  /** Inclusive YYYY-MM-DD bounds of the pay period. */
  startDate: string
  endDate: string
  allocatedSalary: number
  /** Approved leave days falling on working days in range — reported, unpaid. */
  leavesTaken: number
  /** Manual deduction applied after the attendance calculation. */
  reductions: number
  /** Working days in range: calendar days minus Sundays and official holidays. */
  workingDays: number
  /** Working days the employee actually attended (check-in or HR override). */
  presentDays: number
}

export interface SalaryCalcResult {
  /** Calendar days in range, weekends and holidays included — context only. */
  totalDays: number
  workingDays: number
  presentDays: number
  absentDays: number
  /** Leave days that fall on unattended working days — reported, unpaid. */
  excusedLeaveDays: number
  leavesTaken: number
  reductions: number
  allocatedSalary: number
  dailyRate: number
  /** Daily × absent days — the deduction figure shown on the stipend report. */
  absenceDeduction: number
  /** Pay earned for the days actually present, before manual reductions. */
  earnedPay: number
  totalPay: number
}

function nonNegative(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** Inclusive day count between two YYYY-MM-DD dates; 0 if the range is invalid. */
export function daysInRange(startDate: string, endDate: string): number {
  if (!startDate || !endDate || startDate > endDate) return 0
  const start = new Date(`${startDate}T00:00:00`).getTime()
  const end = new Date(`${endDate}T00:00:00`).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  return Math.floor((end - start) / 86_400_000) + 1
}

/** Day keys for every date in the inclusive range, in order; empty if invalid. */
function eachDayYMD(startDate: string, endDate: string): string[] {
  if (daysInRange(startDate, endDate) === 0) return []
  const days: string[] = []
  for (let d = startDate; d <= endDate; d = addDayISO(d)) days.push(d)
  return days
}

/** Next calendar day as YYYY-MM-DD, computed in UTC so IST never shifts it. */
function addDayISO(ymd: string): string {
  const d = new Date(ymd + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}

/**
 * Working days in the inclusive range: every day that is not a weekly off
 * (Sunday) and not an official holiday. Holiday dates outside the range are
 * ignored, matching how the monthly report counts working days.
 */
export function workingDaysInRange(startDate: string, endDate: string, holidayDates: Iterable<string>): number {
  if (daysInRange(startDate, endDate) === 0) return 0
  const holidays = new Set(holidayDates)
  return eachDayYMD(startDate, endDate).filter(d => !isWeekendYMD(d) && !holidays.has(d)).length
}

export function computeSalary(input: SalaryCalcInput): SalaryCalcResult {
  const totalDays = daysInRange(input.startDate, input.endDate)
  const allocatedSalary = nonNegative(input.allocatedSalary)
  const leavesTaken = nonNegative(input.leavesTaken)
  const reductions = nonNegative(input.reductions)
  const workingDays = Math.floor(nonNegative(input.workingDays))
  // Attending more days than exist in the period must not create negative absences.
  const presentDays = Math.min(Math.floor(nonNegative(input.presentDays)), workingDays)

  // Loss-of-pay rate: allocated salary spread over the working days in range.
  const dailyRate = workingDays > 0 ? allocatedSalary / workingDays : 0

  // Classification of the days the employee did NOT attend: covered by approved
  // leave (reported separately) or plain absence. Both are unpaid under the
  // present-days-only policy, so the split is for the report, not the math.
  const uncoveredDays = Math.max(0, workingDays - presentDays)
  const excusedLeaveDays = Math.min(leavesTaken, uncoveredDays)
  const absentDays = uncoveredDays - excusedLeaveDays

  const absenceDeduction = absentDays * dailyRate
  // Final payable is driven purely by attendance: days present × daily rate.
  const earnedPay = presentDays * dailyRate
  const totalPay = Math.max(0, earnedPay - reductions)

  return {
    totalDays, workingDays, presentDays, absentDays, excusedLeaveDays,
    leavesTaken, reductions, allocatedSalary,
    dailyRate, absenceDeduction, earnedPay, totalPay,
  }
}
